<template>
    <section
        id="how-it-works"
        class="w-full bg-[rgb(13,13,13)] py-16 lg:py-32 px-4 lg:px-24"
    >
        <div
            class="max-w-[1040px] mx-auto rounded-2xl lg:rounded-[32px] px-5 py-10 lg:px-20 lg:py-20 border border-white/[0.06] shadow-[0_24px_80px_-12px_rgba(0,0,0,0.8)] backdrop-blur-xl"
            style="background: linear-gradient(180deg, rgba(26,16,48,0.4) 0%, rgba(13,13,13,0.6) 100%)"
        >
            <div class="text-[11px] font-semibold uppercase tracking-[1.5px] text-violet-400 mb-5">
                How It Works
            </div>

            <h2 class="text-white text-2xl lg:text-[42px] font-bold leading-[1.1] tracking-tight mb-6">
                From Your Idea to the Screen
            </h2>

            <p class="text-white/70 text-base lg:text-[17px] leading-[1.6] max-w-[560px] mb-10 lg:mb-16 font-medium">
                As a producer, you control the vision — cast, setting, format. Pitch
                your scene, earn community support, and watch it come to life.
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div
                    v-for="step in steps"
                    :key="step.number"
                    class="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-5 lg:p-8 transition-all duration-300 hover:bg-white/[0.05] shadow-[0_8px_32px_-8px_rgba(0,0,0,0.3)]"
                >
                    <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mb-4 sm:mb-6 overflow-hidden">
                        <component v-if="step.icon" :is="step.icon" class="w-5 h-5 sm:w-6 sm:h-6 text-violet-400" />
                        
                        <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" v-else-if="step.brandIcon === 'thumbs-up'" class="w-7 h-7 sm:w-8 sm:h-8" >
                            <path d="M22 56H14C11.8 56 10 54.2 10 52V34C10 31.8 11.8 30 14 30H22V56Z"
                                stroke="#C9A24D" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M22 30L28 10C29.2 6.8 32.4 5 35.8 5.6C37.6 5.9 39 7.4 39 9.2V22H50.4C53.6 22 56 25 55.4 28.2L51.4 50.2C50.9 53 48.5 55 45.7 55H22"
                                stroke="#A855F7" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M34 30V22" stroke="#C9A24D" stroke-width="2.5" stroke-linecap="round"/>
                            <path d="M42 30V24" stroke="#C9A24D" stroke-width="2.5" stroke-linecap="round"/>
                        </svg>
                        <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" v-else-if="step.brandIcon === 'clapperboard'" class="w-7 h-7 sm:w-8 sm:h-8">
                            <rect x="6" y="22" width="52" height="36" rx="4"
                                stroke="#C9A24D" stroke-width="3.5" stroke-linejoin="round"/>
                            <path d="M6 22L6 16C6 13.8 7.8 12 10 12H54C56.2 12 58 13.8 58 16V22"
                                stroke="#A855F7" stroke-width="3.5" stroke-linejoin="round"/>
                            <path d="M16 12L22 22" stroke="#A855F7" stroke-width="3" stroke-linecap="round"/>
                            <path d="M28 12L34 22" stroke="#A855F7" stroke-width="3" stroke-linecap="round"/>
                            <path d="M40 12L46 22" stroke="#A855F7" stroke-width="3" stroke-linecap="round"/>
                            <path d="M27 34V48L39 41L27 34Z" stroke="#C9A24D" stroke-width="2.5" stroke-linejoin="round" fill="none"/>
                            <circle cx="48" cy="50" r="2" fill="#A855F7"/>
                        </svg>
                    </div>
                    <div class="text-violet-400 text-xs font-semibold tracking-wider mb-3">
                        STEP {{ step.number }}
                    </div>
                    <h3 class="text-white text-lg font-semibold mb-3 tracking-tight">
                        {{ step.title }}
                    </h3>
                    <p class="text-white/60 text-[14px] leading-[1.6] font-medium">
                        {{ step.description }}
                    </p>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { Clapperboard, Star } from 'lucide-vue-next'

const steps = [
    {
        number: '01',
        brandIcon: null,
        icon: Clapperboard,
        title: 'Create Your Pitch',
        description: 'Title your scene, sell the concept, and pick your inspiration cast.',
    },
    {
        number: '02',
        brandIcon: 'thumbs-up',
        icon: null,
        title: 'Get Backed',
        description: 'Members vote to green-light your pitch for casting auditions.',
    },
    {
        number: '03',
        brandIcon: null,
        icon: Star,
        title: 'The Audition Race',
        description: 'Creators submit audition videos to compete for the starring role in your pitch.',
    },
    {
        number: '04',
        brandIcon: 'clapperboard',
        icon: null,
        title: 'Lights, Camera, Action',
        description: 'The winning audition gets produced as an official Naughty America scene.',
    },
]
</script>
